import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { BodyRegularS, CaptionRegularM } from '../../../../styles/globals';
import { IThemeProvider, ITheme } from '../../../../styles/theme';

interface IReportsNestedTableTileCont {
  theme: ITheme;
  checked?: boolean;
  buttonsVisible?: boolean;
  is_anonymized?: boolean;
  depth?: number;
  isLastChild?: boolean;
  isVisualLastChild?: boolean;
  isExpanded?: boolean;
  isActive?: boolean;
  disableHover?: boolean;
}

const TREE_LINE_COLOR = 'rgba(0,0,0,0.15)';
const TREE_LINE_WIDTH = '1px';

export const ReportsNestedTableTileCont = styled(Link)<IReportsNestedTableTileCont>`
  display: flex;
  align-items: center;
  box-sizing: border-box;

  ${({ depth = 0, isLastChild, isActive }) =>
    depth > 0
      ? `
    /* Vertical line */
    &::before {
      content: '';
      position: absolute;
      left: ${10 + 12}px;
      top: 0;
      bottom: ${isLastChild ? '50%' : '0'};
      width: ${TREE_LINE_WIDTH};
      background: ${TREE_LINE_COLOR};
    }

    /* Horizontal elbow */
    &::after {
      content: '';
      position: absolute;
      left: ${10 + 12}px;
      top: 50%;
      width: ${20 + (depth - 1) * 44}px;
      height: ${TREE_LINE_WIDTH};
      background: ${TREE_LINE_COLOR};
    }
  `
      : ''}

  min-height: 40px;
  position: relative;
  z-index: 10;

  ${({ theme, checked, buttonsVisible, is_anonymized, depth = 0, isVisualLastChild, isExpanded, isActive }) => `
    border-radius: ${
      depth === 0
        ? isActive
          ? '8px 8px 0 0'
          : buttonsVisible
          ? '8px 8px 0 8px'
          : '8px'
        : isVisualLastChild && !isExpanded
        ? '0 0 8px 8px'
        : '0'
    };
    width: calc(100% - ${theme.layout.spacing.mikro});

    padding-top: ${theme.layout.spacing.mikro};
    padding-bottom: ${theme.layout.spacing.mikro};
    padding-right: 41px;
    padding-left: ${depth === 0 ? 10 : 10 + depth * 24}px;

    margin-bottom: ${depth === 0 ? (isActive ? '0' : theme.layout.spacing.mikro) : '0'};
    opacity: ${is_anonymized ? '1' : '0.75'};
    background: ${checked ? theme.colors.secondary[30] : theme.colors.primary[10]};
    border-bottom: ${
      depth === 0
        ? !isActive
          ? `1px solid ${theme.colors.primary[70]}`
          : 'none'
        : isVisualLastChild && !isExpanded
        ? `1px solid ${theme.colors.primary[70]}`
        : 'none'
    };
    color: ${
      checked || buttonsVisible
        ? theme.colors.neutral.primary[100]
        : theme.colors.neutral.primary[80]
    };

    ${
      is_anonymized
        ? `
          cursor: pointer;
          &:hover { 
            background: ${theme.colors.primary[10]};
          }
        `
        : `
          cursor: default;
        `
    }
  `}

  ${({ is_anonymized, disableHover, theme }) =>
    is_anonymized && !disableHover
      ? `
      &:hover {
        background: ${theme.colors.primary[30]};
      }

      &:hover::before,
      &:hover::after {
        background: ${theme.colors.primary[30]};
      }
    `
      : ''}
`;

export const ReportsNestedTableTileContentCont = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin: 0 0 0 5px;
`;

export const ReportGroup = styled(BodyRegularS)`
  display: flex;
  align-items: center;
  min-width: 150px;
  max-width: 200px;
  width: 100%;
  @media (min-width: 1360px) {
    min-width: 180px;
    max-width: 300px;
  }
  //width: 180px;
  ${({ theme }: IThemeProvider) => `
      color: ${theme.colors.neutral.primary[90]};
  `};
`;

export const ReportsRegularNestedTableTileEl = styled(ReportGroup)`
  text-transform: capitalize;
  padding-right: 6px;
`;

export const ReportsLowercaseNestedTableTileEl = styled(ReportGroup)`
  padding-right: 6px;
`;

export const ReportTileCont = styled.div`
  position: relative;
`;
export const DownloadIconCont = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 1500;
  top: 11px;
  right: 25px;
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

export const HoverDownloadCont = styled.div`
  display: block;
  &:hover {
    background: red;
    //display: flex;
  }
`;

export const NoReport = styled(CaptionRegularM)`
  position: absolute;
  top: 40%;
  right: 5%;
  z-index: 1000;
  font-size: 12px;
  opacity: 0.8;
`;

export const TooltipContainer = styled.div`
  text-align: justify;
  ${({ theme }: IThemeProvider) => `
  margin: 0 0 0 ${theme.layout.spacing.mikro};
`}
`;

export const TooltipContent = styled.div`
  display: flex;
  justify-content: flex-start;
  text-align: left;
`;

export const TooltipList = styled.div`
  display: flex;
  justify-content: flex-start;
  text-align: left;
  flex-direction: column;
`;

export const ResizeBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  max-width: 20px;
  margin-right: 20px;
`;

export const SubReportsContainer = styled.div<{ $depth: number }>`
  display: flex;
  flex-direction: column;

  max-height: 400px;
  overflow: auto;

  /* Firefox */
  scrollbar-width: none;

  /* IE / Edge (legacy) */
  -ms-overflow-style: none;

  /* Chrome / Safari / Edge */
  &::-webkit-scrollbar {
    display: none;
  }

  ${({ theme, $depth }: IThemeProvider & { $depth: number }) => `
    margin-bottom: ${$depth === 0 ? theme.layout.spacing.mikro : '0'};
  `}
`;
